import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiBookOpen, FiCalendar, FiUsers, FiSave, FiVideo, FiMessageCircle, FiUnlock } from 'react-icons/fi';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &:disabled {
    background-color: var(--background-secondary);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
  min-height: 44px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: var(--background-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--background-secondary);
    border-color: var(--border-dark);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const SaveButton = styled(Button)`
  background-color: var(--primary-color);
  color: var(--text-inverse);
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ToggleCard = styled.label`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--border-color);
  background: var(--background-secondary);
  cursor: pointer;
`;

const ToggleContent = styled.div`
  display: flex;
  gap: 0.75rem;
  color: var(--text-primary);
`;

const ToggleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ToggleTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
`;

const ToggleDescription = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
  line-height: 1.4;
`;

const ToggleInput = styled.input`
  width: 2.75rem;
  height: 1.5rem;
  appearance: none;
  border-radius: 999px;
  background: var(--border-color);
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 0.1875rem;
    left: 0.1875rem;
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 50%;
    background: #fff;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  &:checked {
    background: var(--primary-color);
  }

  &:checked::after {
    transform: translateX(1.25rem);
  }
`;

function EditCourseModal({ isOpen, onClose, course, onCourseUpdated }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    instructorId: '',
    maxStudents: 25,
    startDate: '',
    endDate: '',
    isActive: true,
    openEnrollment: true,
    virtualLink: '',
    whatsappGroupLink: ''
  });
  const [instructors, setInstructors] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (course) {
      setFormData({
        code: course.code || '',
        name: course.name || '',
        description: course.description || '',
        instructorId: course.instructor_id || '',
        maxStudents: course.max_students || 25,
        startDate: course.start_date ? course.start_date.split('T')[0] : '',
        endDate: course.end_date ? course.end_date.split('T')[0] : '',
        isActive: course.is_active !== undefined ? course.is_active : true,
        openEnrollment: course.open_enrollment !== undefined ? Boolean(course.open_enrollment) : course.openEnrollment !== undefined ? Boolean(course.openEnrollment) : true,
        virtualLink: course.virtual_link || '',
        whatsappGroupLink: course.whatsapp_group_link || ''
      });
      setErrors({});
    }
  }, [course]);

  useEffect(() => {
    if (isOpen) {
      fetchInstructors();
    }
  }, [isOpen]);

  const fetchInstructors = async () => {
    try {
      const response = await apiService.getInstructors();
      setInstructors(response.instructors || []);
    } catch (error) {
      console.error('Error fetching instructors:', error);
      setInstructors([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Course code is required';
    } else if (formData.code.length > 20) {
      newErrors.code = 'Course code must be 20 characters or less';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Course name is required';
    }

    if (!formData.instructorId) {
      newErrors.instructorId = 'Instructor is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (formData.maxStudents < 1 || formData.maxStudents > 200) {
      newErrors.maxStudents = 'Maximum students must be between 1 and 200';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await apiService.updateCourse(course.id, formData);
      
      onCourseUpdated(response.course);
      onClose();
      
    } catch (error) {
      console.error('Error updating course:', error);
      setErrors({ submit: 'Failed to update course. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!course) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>Edit Course</ModalTitle>
              <CloseButton onClick={onClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label>
                    <FiBookOpen size={16} />
                    Course Code
                  </Label>
                  <Input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                    placeholder="e.g., FAITH101"
                  />
                  {errors.code && <ErrorMessage>{errors.code}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiBookOpen size={16} />
                    Course Name
                  </Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter course name"
                  />
                  {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>
                  <FiBookOpen size={16} />
                  Description
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter course description"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  <FiVideo size={16} />
                  Virtual Classroom Link (Microsoft Teams)
                </Label>
                <Input
                  type="url"
                  value={formData.virtualLink}
                  onChange={(e) => handleInputChange('virtualLink', e.target.value)}
                  placeholder="https://teams.microsoft.com/l/meetup-join/..."
                />
                <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  Optional: Add the Microsoft Teams meeting link for live class sessions
                </small>
              </FormGroup>

              <FormGroup>
                <Label>
                  <FiMessageCircle size={16} />
                  WhatsApp Group Link
                </Label>
                <Input
                  type="url"
                  value={formData.whatsappGroupLink}
                  onChange={(e) => handleInputChange('whatsappGroupLink', e.target.value)}
                  placeholder="https://chat.whatsapp.com/..."
                />
                <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  Optional: Add the WhatsApp group link for course discussions and updates
                </small>
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>
                    <FiUsers size={16} />
                    Instructor
                  </Label>
                  {user?.role === 'admin' ? (
                    <Select
                      value={formData.instructorId}
                      onChange={(e) => handleInputChange('instructorId', e.target.value)}
                    >
                      <option value="">Select an instructor</option>
                      {instructors.map(instructor => (
                        <option key={instructor.id} value={instructor.id}>
                          {instructor.first_name} {instructor.last_name}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type="text"
                      value={instructors.find(inst => inst.id === parseInt(formData.instructorId))?.first_name + ' ' + instructors.find(inst => inst.id === parseInt(formData.instructorId))?.last_name || 'Loading...'}
                      disabled
                      style={{ backgroundColor: 'var(--background-secondary)', color: 'var(--text-muted)' }}
                    />
                  )}
                  {errors.instructorId && <ErrorMessage>{errors.instructorId}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiUsers size={16} />
                    Max Students
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    value={formData.maxStudents}
                    onChange={(e) => handleInputChange('maxStudents', parseInt(e.target.value))}
                  />
                  {errors.maxStudents && <ErrorMessage>{errors.maxStudents}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>
                    <FiCalendar size={16} />
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                  />
                  {errors.startDate && <ErrorMessage>{errors.startDate}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <Label>
                    <FiCalendar size={16} />
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                  />
                  {errors.endDate && <ErrorMessage>{errors.endDate}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>
                  <FiBookOpen size={16} />
                  Status
                </Label>
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => handleInputChange('isActive', e.target.value === 'active')}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </FormGroup>

              <ToggleCard>
                <ToggleContent>
                  <FiUnlock size={18} />
                  <ToggleText>
                    <ToggleTitle>Open Enrollment</ToggleTitle>
                    <ToggleDescription>
                      Keep this course visible in the course catalog for student registration regardless of the enrollment period.
                    </ToggleDescription>
                  </ToggleText>
                </ToggleContent>
                <ToggleInput
                  type="checkbox"
                  checked={Boolean(formData.openEnrollment)}
                  onChange={(e) => handleInputChange('openEnrollment', e.target.checked)}
                />
              </ToggleCard>

              {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

              <ButtonGroup>
                <CancelButton type="button" onClick={onClose} disabled={loading}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FiSave size={16} />
                      Update Course
                    </>
                  )}
                </SaveButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default EditCourseModal;
